import { apiRequest } from '@/utils/api-request';
import type { RunChatLink } from '@/stores/operator-conversation-store';
import { clearNodeResults } from '@/pages/workflow/helper/clear-node-results';
import { promoteGroupChildrenOnDelete } from '@/pages/workflow/helper/group-nodes';
import { CustomNode, NodeType } from '@/types/node-types';
import { CustomEdge } from '@/types/edge-types';
import { useWorkflowStore } from '@/stores/workflow-store';
import {
  validateOrphanedReferences,
  repairWorkflowOrphanedReferences,
  useNodeStore,
} from '@/stores/node-store';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';
import { analytics } from '@/lib/analytics';

export interface RunWorkflowResponse {
  data: {
    execution_summary: {
      total_nodes: number;
      errors: string[];
      execution_order: string[];
      entry_nodes: string[];
    };
    message: string;
    session_id: string;
    success: boolean;
    workflow: {
      edges: CustomEdge[];
      nodes: CustomNode[];
    };
    description: string;
    edges: string[];
    entry_node_id: string;
    execution_metadata: string;
    global_context: string;
    id: string;
    metadata: string;
    name: string;
    nodes: string[];
    websocket_url: string;
  };
  detail?: string;
}

export interface AsyncRunWorkflowBody {
  success: boolean;
  message: string;
  session_id: string;
  workflow_id: string;
  job_id: string;
  run_id?: string | null;
}

export type RunWorkflowResult = {
  detail?: string;
  message?: string;
  success?: boolean;
  statusCode?: number;
  data?: AsyncRunWorkflowBody;
  runId: string | null;
};

// Already derived from the FE "mode". When asking isn't allowed the keys are
// omitted entirely, so the BE defaults preserve baseline behaviour.
export interface ClarificationRunOptions {
  allowClarifications: boolean;
  /** Bounded timeout in seconds, or ``null`` for "ask and wait"
   *  (indefinite — the BE parks with no timeout fallback). */
  clarificationTimeoutSecs: number | null;
  /**
   * Always send ``allow_clarifications`` — including an explicit
   * ``false`` — instead of omitting the keys in auto mode. Used when the
   * workflow declares a ``default_clarification_mode``: the BE falls back
   * to that metadata default whenever the keys are absent, so the pill's
   * session choice must be sent explicitly to win. Left unset for
   * workflows without a default so their payloads are unchanged.
   */
  forceExplicit?: boolean;
}

export async function runWorkflow(
  id: string,
  sessionId: string,
  name: string,
  nodes: CustomNode[],
  edges: CustomEdge[],
  orgId: string,
  clarificationOptions?: ClarificationRunOptions,
  chatLink?: RunChatLink | null,
): Promise<RunWorkflowResult> {
  console.log('running pipeline');

  // Filter out SelectNode and GroupNode type nodes before sending to backend.
  // This payload is also replayed later for the "past runs" feature (see
  // loadRunData), which doesn't know about groups, so grouped children are
  // promoted to absolute positions/cleared parentId before the group
  // container is stripped — otherwise they'd point at a missing parent.
  const nonExecutableNodeIds = new Set(
    nodes
      .filter(
        (node) =>
          node.type === NodeType.SelectNode || node.type === NodeType.GroupNode,
      )
      .map((node) => node.id),
  );
  const groupNodeIds = new Set(
    nodes.filter((node) => node.type === NodeType.GroupNode).map((n) => n.id),
  );
  const nodesWithoutSelectNodes = promoteGroupChildrenOnDelete(
    nodes,
    groupNodeIds,
  ).filter((node) => node.type !== NodeType.SelectNode);
  const filteredNodes = clearNodeResults(nodesWithoutSelectNodes);

  // Filter out edges connected to SelectNode / GroupNode nodes (either side —
  // group containers are stripped from the node list, so an edge still
  // sourced from one would reference a missing node in the run payload).
  const filteredEdges = edges.filter(
    (edge) =>
      !nonExecutableNodeIds.has(edge.target) &&
      !nonExecutableNodeIds.has(edge.source),
  );

  // Send full React Flow nodes/edges using the AdvancedWorkflowPayload shape
  // (the same structure used by persist-workflow). Previously this endpoint
  // only sent a reduced, execution-oriented representation, which was enough
  // to run a workflow but did not contain all the information needed to
  // faithfully reconstruct the editor state (e.g., full node config and
  // graph structure). By reusing the persist-workflow format here, the
  // backend can store the exact graph that was run and later reload it
  // verbatim for the "past runs" feature.

  const currentWorkflow = useWorkflowStore.getState().currentWorkflow;

  const payload: Record<string, unknown> = {
    id,
    session_id: sessionId,
    name,
    nodes: filteredNodes,
    edges: filteredEdges,
    metadata: currentWorkflow?.metadata,
    ...(chatLink
      ? {
          chat_conversation_id: chatLink.conversationId,
          chat_agent_id: chatLink.agentId,
        }
      : {}),
    // Keys are emitted only when the toggle is on, leaving the BE defaults in
    // charge otherwise. forceExplicit instead sends allow_clarifications:
    // false, so a session choice can override the workflow default.
    ...(clarificationOptions &&
    (clarificationOptions.allowClarifications ||
      clarificationOptions.forceExplicit)
      ? {
          allow_clarifications: clarificationOptions.allowClarifications,
          ...(clarificationOptions.allowClarifications
            ? {
                // ``null`` => "ask and wait" (indefinite); a number => bounded.
                clarification_timeout_secs:
                  clarificationOptions.clarificationTimeoutSecs,
              }
            : {}),
        }
      : {}),
  };

  console.log('workflow json', payload);

  // ==========================================================================
  // Validate and repair orphaned references (corrupted workflow data)
  // - Validates for orphaned node/port references
  // - Auto-repairs if corruption detected
  // - Sends detailed report to PostHog for dev team awareness
  // - Persists repaired workflow to prevent future corruption
  // ==========================================================================
  const orphanedRefValidation = validateOrphanedReferences(
    filteredNodes,
    filteredEdges,
  );

  let repairedNodes = filteredNodes;
  let repairedEdges = filteredEdges;

  if (!orphanedRefValidation.isValid) {
    // Repair corrupted workflow data
    const repairResult = repairWorkflowOrphanedReferences(
      filteredNodes,
      filteredEdges,
      orphanedRefValidation,
    );

    // Use repaired data for this workflow run
    repairedNodes = repairResult.repairedNodes;
    repairedEdges = repairResult.repairedEdges;

    // Update store with repaired data
    useNodeStore.getState().setNodes(repairResult.repairedNodes);
    useNodeStore.getState().setEdges(repairResult.repairedEdges);

    // Send comprehensive report to PostHog
    const workflowUrl = `${window.location.origin}/workflow/${id}`;
    const error = new Error('Orphaned references detected in workflow');
    analytics.captureException(error, {
      workflowId: id,
      sessionId: sessionId,
      workflowName: name,
      orgId: orgId,
      url: workflowUrl,
      errors: orphanedRefValidation.errors,
      repairDetails: repairResult.details,
      repairsMade: repairResult.repairsMade,
      component: 'runWorkflow',
      errorType: 'orphanedReferences',
      action: 'auto-repaired',
    });

    // Persist repaired workflow to backend
    try {
      const currentWorkflow = useWorkflowStore.getState().currentWorkflow;
      await useWorkflowStore.getState().saveWorkflow({
        orgId,
        workflowId: id,
        name,
        nodes: repairResult.repairedNodes,
        edges: repairResult.repairedEdges,
        description: currentWorkflow?.description || '',
        updateCurrentWorkflow: false, // Don't update current workflow metadata
      });
      console.log('[runWorkflow] Successfully persisted repaired workflow');
    } catch (saveError) {
      console.error(
        '[runWorkflow] Failed to persist repaired workflow:',
        saveError,
      );
      // Don't block workflow execution if save fails
    }
  }

  // Clear field validation errors if validation passes
  useWorkflowValidationStore.getState().clearFieldErrors();

  const finalPayload: Record<string, unknown> = {
    ...payload,
    nodes: repairedNodes,
    edges: repairedEdges,
  };

  // POST to /run with JSON payload
  const response = await apiRequest<AsyncRunWorkflowBody>({
    url: `/workflow/${orgId}/${id}/${sessionId}/async-run-workflow`,
    method: 'POST',
    target: 'workflow',
    body: finalPayload, // Send JSON payload with repaired data
  });

  if (response?.detail) {
    return {
      detail: response.detail || 'Failed to run workflow',
      runId: null,
    };
  }

  return {
    ...response,
    runId: response.data?.run_id ?? null,
  };
}
